import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/dashboard/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { getInvoices, getExpenses, getSales, getPayments } from "../services/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  ArrowUpRight,
  ArrowDownLeft,
  Filter,
  Download,
  Calendar,
  CreditCard,
  Receipt,
  FileText,
  Users,
  Package,
  Eye,
  X
} from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { downloadTransactionsCSV } from '../utils/csvDownload';
import MobileDateInput from '@/components/ui/MobileDateInput';
import { mobileAmountClasses } from '../utils/mobileUtils';

const Transactions = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [summary, setSummary] = useState({
    totalMoneyIn: 0,
    totalMoneyOut: 0,
    netFlow: 0,
    transactionCount: 0
  });
  
  const [filters, setFilters] = useState({
    date: '',
    type: 'all', // all, money_in, money_out
    category: 'all',
    paymentMethod: 'all'
  });

  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionDetail, setShowTransactionDetail] = useState(false);

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [transactions, filters]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      
      // Fetch data from multiple sources
      const [invoicesRes, expensesRes, salesRes, paymentsRes] = await Promise.all([
        getInvoices().catch(() => ({ invoices: [] })),
        getExpenses().catch(() => ({ expenses: [] })),
        getSales().catch(() => ({ sales: [] })),
        getPayments().catch(() => ({ payments: [] }))
      ]);

      // Combine all transactions
      const allTransactions = [
        // Money In - Sales/Invoices
        ...(salesRes.sales || []).map(sale => ({
          id: `sale-${sale.id}`,
          type: 'money_in',
          category: 'Sales',
          description: `Sale to ${sale.customer?.name || 'Walk-in Customer'}`,
          amount: sale.total_amount,
          date: sale.created_at,
          paymentMethod: sale.payment_method || 'Cash',
          reference: sale.id,
          icon: TrendingUp,
          color: 'text-green-600'
        })),
        
        // Money In - Invoice Payments
        ...(invoicesRes.invoices || [])
          .filter(invoice => invoice.status === 'paid')
          .map(invoice => ({
            id: `invoice-${invoice.id}`,
            type: 'money_in',
            category: 'Invoice Payment',
            description: `Payment for Invoice #${invoice.invoice_number}`,
            amount: invoice.total_amount || 0,
            date: invoice.updated_at,
            paymentMethod: 'Bank Transfer',
            reference: invoice.invoice_number,
            icon: FileText,
            color: 'text-green-600'
          })),

        // Money Out - Expenses
        ...(expensesRes.expenses || []).map(expense => ({
          id: `expense-${expense.id}`,
          type: 'money_out',
          category: expense.category || 'Business Expense',
          description: expense.description,
          amount: expense.amount,
          date: expense.date || expense.created_at,
          paymentMethod: 'Cash',
          reference: expense.id,
          icon: Receipt,
          color: 'text-red-600'
        })),

        // Money Out - Referral Withdrawals
        ...(user?.total_withdrawn > 0 ? [{
          id: 'referral-withdrawal',
          type: 'money_out',
          category: 'Referral Withdrawal',
          description: 'Referral earnings withdrawal',
          amount: user.total_withdrawn,
          date: new Date().toISOString(),
          paymentMethod: 'Bank Transfer',
          reference: 'REF-WITHDRAWAL',
          icon: Users,
          color: 'text-red-600'
        }] : [])
      ];

      // Sort by date (newest first)
      allTransactions.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setTransactions(allTransactions);
      calculateSummary(allTransactions);
      
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateSummary = (transactionList) => {
    const moneyIn = transactionList
      .filter(t => t.type === 'money_in')
      .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
    
    const moneyOut = transactionList
      .filter(t => t.type === 'money_out')
      .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);

    setSummary({
      totalMoneyIn: moneyIn,
      totalMoneyOut: moneyOut,
      netFlow: moneyIn - moneyOut,
      transactionCount: transactionList.length
    });
  };

  const applyFilters = () => {
    let filtered = [...transactions];

    // Date filter
    if (filters.date) {
      filtered = filtered.filter(t => new Date(t.date) >= new Date(filters.date));
    }

    // Type filter
    if (filters.type !== 'all') {
      filtered = filtered.filter(t => t.type === filters.type);
    }

    // Category filter
    if (filters.category !== 'all') {
      filtered = filtered.filter(t => t.category === filters.category);
    }

    // Payment method filter
    if (filters.paymentMethod !== 'all') {
      filtered = filtered.filter(t => t.paymentMethod === filters.paymentMethod);
    }

    setFilteredTransactions(filtered);
    calculateSummary(filtered);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount);
  };

  const getUniqueCategories = () => {
    const categories = [...new Set(transactions.map(t => t.category))];
    return categories.filter(Boolean);
  };

  const getUniquePaymentMethods = () => {
    const methods = [...new Set(transactions.map(t => t.paymentMethod))];
    return methods.filter(Boolean);
  };

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setShowTransactionDetail(true);
  };

  const closeTransactionDetail = () => {
    setSelectedTransaction(null);
    setShowTransactionDetail(false);
  };

  const exportTransactions = () => {
    try {
      if (filteredTransactions.length === 0) {
        // You can add a toast notification here if needed
        console.warn('No transactions to export');
        return;
      }

      // Prepare transactions data with proper formatting
      const transactionsData = filteredTransactions.map(transaction => ({
        date: format(new Date(transaction.date), 'yyyy-MM-dd'),
        time: format(new Date(transaction.date), 'HH:mm:ss'),
        customer_name: transaction.customer_name || 'N/A',
        product_name: transaction.product_name || transaction.description || 'N/A',
        quantity: transaction.quantity || 1,
        unit_price: transaction.unit_price || transaction.amount || 0,
        total_amount: transaction.amount || 0,
        payment_method: transaction.paymentMethod || 'N/A',
        transaction_type: transaction.type === 'money_in' ? 'Money In' : 'Money Out',
        status: transaction.status || 'Completed'
      }));

      // Use the proper CSV utility
      downloadTransactionsCSV(transactionsData, `transactions-${format(new Date(), 'yyyy-MM-dd')}`);
    } catch (error) {
      console.error('Error exporting transactions:', error);
      // You can add error toast notification here if needed
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-3 sm:p-4 space-y-4 sm:space-y-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Transaction History</h1>
          </div>
          <div className="text-center py-8">Loading transactions...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-3 sm:p-4 space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Transaction History</h1>
            <p className="text-gray-600 text-sm sm:text-base">
              Complete overview of money flowing in and out of your business
            </p>
          </div>
        <Button onClick={exportTransactions} variant="outline">
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Money In</p>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(summary.totalMoneyIn)}
                </p>
              </div>
              <ArrowDownLeft className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Money Out</p>
                <p className="text-2xl font-bold text-red-600">
                  {formatCurrency(summary.totalMoneyOut)}
                </p>
              </div>
              <ArrowUpRight className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Net Flow</p>
                <p className={`text-2xl font-bold ${summary.netFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(summary.netFlow)}
                </p>
              </div>
              <DollarSign className={`h-8 w-8 ${summary.netFlow >= 0 ? 'text-green-600' : 'text-red-600'}`} />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Transactions</p>
                <p className="text-2xl font-bold">{summary.transactionCount}</p>
              </div>
              <CreditCard className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
          <CardDescription>
            Filter transactions by date, type, category, or payment method
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <MobileDateInput
                value={filters.date}
                onChange={(e) => setFilters({...filters, date: e.target.value})}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={filters.type} onValueChange={(value) => setFilters({...filters, type: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Transactions</SelectItem>
                  <SelectItem value="money_in">Money In</SelectItem>
                  <SelectItem value="money_out">Money Out</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={filters.category} onValueChange={(value) => setFilters({...filters, category: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {getUniqueCategories().map(category => (
                    <SelectItem key={category} value={category}>{category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Select value={filters.paymentMethod} onValueChange={(value) => setFilters({...filters, paymentMethod: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Methods</SelectItem>
                  {getUniquePaymentMethods().map(method => (
                    <SelectItem key={method} value={method}>{method}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="flex gap-2 mt-4">
            <Button 
              variant="outline" 
              onClick={() => setFilters({
                dateFrom: '',
                dateTo: '',
                type: 'all',
                category: 'all',
                paymentMethod: 'all'
              })}
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Transaction Details</CardTitle>
          <CardDescription>
            Detailed view of all your business transactions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredTransactions.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date & Time</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Payment Method</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.map((transaction) => {
                  const IconComponent = transaction.icon;
                  return (
                    <TableRow 
                      key={transaction.id} 
                      className="cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => handleViewTransaction(transaction)}
                    >
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {format(new Date(transaction.date), 'MMM dd, yyyy')}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {format(new Date(transaction.date), 'HH:mm')}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <IconComponent className={`h-4 w-4 ${transaction.color}`} />
                          <Badge variant={transaction.type === 'money_in' ? 'default' : 'destructive'}>
                            {transaction.type === 'money_in' ? 'Money In' : 'Money Out'}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
                          {transaction.category}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{transaction.description}</div>
                          <div className="text-sm text-muted-foreground">
                            Ref: {transaction.reference}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {transaction.paymentMethod}
                        </Badge>
                      </TableCell>
                      <TableCell className={`text-right font-medium ${transaction.color}`}>
                        <div className={`${mobileAmountClasses.small} ${mobileAmountClasses.container}`}>
                          {transaction.type === 'money_in' ? '+' : '-'}{formatCurrency(transaction.amount)}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent row click
                            handleViewTransaction(transaction);
                          }}
                          className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <Alert>
              <AlertDescription>
                No transactions found matching your criteria. Try adjusting your filters.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Transaction Detail Modal */}
      {showTransactionDetail && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Transaction Details</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeTransactionDetail}
                className="h-8 w-8 p-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            
            <div className="p-4 space-y-4">
              {/* Transaction Type */}
              <div className="flex items-center justify-center">
                <div className="flex items-center gap-2">
                  {React.createElement(selectedTransaction.icon, { className: `h-6 w-6 ${selectedTransaction.color}` })}
                  <Badge variant={selectedTransaction.type === 'money_in' ? 'default' : 'destructive'} className="text-sm">
                    {selectedTransaction.type === 'money_in' ? 'Money In' : 'Money Out'}
                  </Badge>
                </div>
              </div>

              {/* Amount */}
              <div className="text-center py-4 bg-gray-50 rounded-lg">
                <p className={`${mobileAmountClasses.large} ${mobileAmountClasses.container} ${selectedTransaction.color}`}>
                  {selectedTransaction.type === 'money_in' ? '+' : '-'}{formatCurrency(selectedTransaction.amount)}
                </p>
                <p className="text-sm text-gray-500 mt-1">Transaction Amount</p>
              </div>

              {/* Details */}
              <div className="space-y-3">
                <div>
                  <p className="text-sm font-medium text-gray-500">Description</p>
                  <p className="text-gray-900">{selectedTransaction.description}</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-500">Category</p>
                  <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-1 text-sm font-medium text-blue-700">
                    {selectedTransaction.category}
                  </span>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-500">Payment Method</p>
                  <Badge variant="outline" className="mt-1">
                    {selectedTransaction.paymentMethod}
                  </Badge>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-500">Reference</p>
                  <p className="text-gray-900 font-mono text-sm">{selectedTransaction.reference}</p>
                </div>

                <div>
                  <p className="text-sm font-medium text-gray-500">Date & Time</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-gray-900">{format(new Date(selectedTransaction.date), 'MMM dd, yyyy')}</p>
                      <p className="text-sm text-gray-500">{format(new Date(selectedTransaction.date), 'HH:mm')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 border-t bg-gray-50">
              <Button
                onClick={closeTransactionDetail}
                className="w-full"
                variant="outline"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
      </div>
    </DashboardLayout>
  );
};

export default Transactions;

